import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EventBody, EventDetailDTO, EventExtra, EventType, GuestTier } from "../../../../models/EventDTO";
import S from "./strings";
import { Modal, aInput, conMoneda, deInput, errorText, numOrNull } from "./comun";
import { PreviewDTO } from "../../../../models/EventDTO";
import { QvpCuerpo } from "./queVaAPasar";
import { CurrencyRef } from "../../../../models/WorkshopDTO";
import { FechaHoraDMY } from "../../../../components/fechas/camposFecha";

interface Props {
	campusId: number;
	evento: EventDetailDTO | null;
	/** Moneda del plantel (para las etiquetas de precio al crear). */
	currency?: CurrencyRef | null;
	onClose: () => void;
	onSaved: (e: EventDetailDTO, texto: string) => void;
}

const TIPOS: EventType[] = ["graduacion", "batalla", "seminario", "otro"];
const PAQUETES = [2, 3, 4, 5];
const txt = (v: any) => (v === null || v === undefined ? "" : String(v));

/** Crear / editar un evento: datos, precios, paquetes de invitados y extras, en una sola pantalla. Los costos se agregan en Resultado. */
const EventForm: React.FC<Props> = ({ campusId, evento, currency, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const cur = evento?.currency || currency || null;
	const [f, setF] = useState({
		type: (evento?.type || "graduacion") as EventType,
		name: evento?.name || "",
		date: aInput(evento?.dateTime || evento?.date),
		venue: evento?.venue || "",
		capacity: txt(evento?.capacity),
		tableSize: txt(evento?.tableSize),
		packagePrice: txt(evento?.packagePrice),
		extraGuestPrice: txt(evento?.extraGuestPrice),
		childPrice: txt(evento?.childPrice),
		notes: evento?.notes || ""
	});
	// Paquete Invitados: renglones fijos de 2 a 5 (más los que ya tuviera el evento); vacío = sin paquete
	const [tiers, setTiers] = useState<{ guests: number; total: string; freeChild: boolean }[]>(() => {
		const guardados = evento?.guestTiers || [];
		const cuantos = Array.from(new Set([...PAQUETES, ...guardados.map(t => t.guests)])).sort((a, b) => a - b);
		return cuantos.map(n => { const t = guardados.find(x => x.guests === n); return { guests: n, total: t ? String(t.total) : "", freeChild: !!t?.freeChild }; });
	});
	const [extras, setExtras] = useState<{ key?: number; concept: string; price: string }[]>((evento?.extras || []).map(x => ({ key: x.key, concept: x.concept, price: String(x.price) })));
	const setExtra = (i: number, k: "concept" | "price", v: string) => setExtras(xs => xs.map((x, j) => j === i ? { ...x, [k]: v } : x));
	const [busy, setBusy] = useState<boolean>(false);
	// Editar con cambios de dinero: primero «Qué Va a Pasar» (el servidor corre el guardado y lo deshace) y luego se confirma
	const [pv, setPv] = useState<PreviewDTO | null>(null);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));
	const valido = f.name.trim().length > 1 && f.date !== "";

	const extra = numOrNull(f.extraGuestPrice) || 0;
	const setTier = (i: number, k: "total" | "freeChild", v: any) => setTiers(ts => ts.map((x, j) => j === i ? { ...x, [k]: v } : x));

	const cuerpo = (): EventBody => {
		const paquetes: GuestTier[] = tiers.filter(t => t.total.trim() !== "" && !isNaN(Number(t.total)) && Number(t.total) > 0)
			.map(t => ({ guests: t.guests, total: Number(t.total), freeChild: t.freeChild }));
		const vendibles: EventExtra[] = extras.filter(x => x.concept.trim() && x.price.trim() !== "" && !isNaN(Number(x.price)))
			.map(x => ({ key: x.key, concept: x.concept.trim(), price: Number(x.price) }));
		const body: EventBody = {
			type: f.type, name: f.name.trim(), date: deInput(f.date) as string, venue: f.venue.trim() || null,
			capacity: numOrNull(f.capacity), tableSize: numOrNull(f.tableSize), packagePrice: numOrNull(f.packagePrice),
			extraGuestPrice: numOrNull(f.extraGuestPrice), childPrice: numOrNull(f.childPrice), guestTiers: paquetes, extras: vendibles, notes: f.notes.trim() || null
		};
		return body;
	};
	const guardarYa = () => {
		const body = cuerpo();
		setBusy(true); setError(null);
		const req = evento ? http.eventsService.update(campusId, { ...body, id: evento.id }) : http.eventsService.create(campusId, body);
		req.then(r => onSaved(r.data, evento ? S.guardado : S.creado)).catch(e => { setError(errorText(e)); setBusy(false); setPv(null); });
	};
	const guardar = () => {
		if (!evento) { guardarYa(); return; }
		setBusy(true); setError(null);
		http.eventsService.previewUpdate(campusId, { eventId: evento.id, action: "update", body: cuerpo() })
			.then(r => {
				const p = r.data;
				if (p.ok && p.created.length === 0 && p.cancelled.length === 0) { guardarYa(); return; }
				setPv(p); setBusy(false);
			})
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};

	const campo = (k: keyof typeof f, label: string, tipo = "number", flex = "1 1 120px") => (
		<label className="wau-campo" style={{ flex }}><span className="wau-lab">{label}</span>
			<input type={tipo} min={0} step={tipo === "number" ? "0.01" : undefined} className={"wau-entrada" + (tipo === "number" ? " wau-num" : "")} value={f[k] as string} onChange={e => set(k, e.target.value)} /></label>
	);
	/** "Ahorra L 1,000" sin centavos: la columna es angosta. */
	const ahorroCorto = (v: number) => S.ahorra(`${cur?.symbol || ""} ${Math.round(v).toLocaleString("en-US")}`.trim());

	return (
		<Modal title={evento ? S.editar + " " + evento.name : S.nuevo} onClose={onClose} ancho={960}>
			{pv ? <>
				<QvpCuerpo pv={pv} titulo={S.qvp + " · " + S.qvpEditarTitulo} />
				<div className="wau-acciones">
					<button type="button" className="wau-btn s" onClick={() => setPv(null)} disabled={busy}>{S.volver}</button>
					<button type="button" className="wau-btn p" onClick={guardarYa} disabled={busy || !pv.ok}>{busy ? S.guardando : S.confirmar}</button>
				</div>
			</> : <>
			<div className="wev-evform">
				<div className="wau-fila">
					<label className="wau-campo" style={{ flex: "0 1 150px" }}><span className="wau-lab">{S.tipo}</span>
						<select className="wau-entrada" value={f.type} onChange={e => set("type", e.target.value)}>
							{TIPOS.map(t => <option key={t} value={t}>{S.tipos[t]}</option>)}
						</select></label>
					<label className="wau-campo" style={{ flex: "2 1 220px" }}><span className="wau-lab">{S.nombre}</span>
						<input className="wau-entrada" value={f.name} onChange={e => set("name", e.target.value)} autoFocus /></label>
					{campo("venue", S.lugar, "text", "2 1 200px")}
				</div>
				<div className="wau-fila">
					<div className="wau-campo" style={{ flex: "0 0 300px", maxWidth: "100%" }}><span className="wau-lab">{S.fecha}</span>
						<FechaHoraDMY value={f.date} onChange={v => set("date", v)} /></div>
					{campo("capacity", S.cupo, "number", "1 1 110px")}
					{campo("tableSize", S.porMesa, "number", "1 1 130px")}
				</div>
				<div className="wev-evform-dos">
					<div>
						<div className="wau-fila">
							{campo("packagePrice", conMoneda(S.paquete, cur))}
							{campo("extraGuestPrice", conMoneda(S.invitadoExtra, cur))}
							{campo("childPrice", conMoneda(S.nino, cur))}
						</div>
						<span className="wau-lab" style={{ display: "block", marginTop: 10 }}>{conMoneda(S.extras, cur)}</span>
						{extras.map((x, i) => (
							<div key={i} className="wev-costo" style={{ marginTop: 4 }}>
								<input className="wau-entrada" aria-label={S.concepto} placeholder={S.extraEjemplo} value={x.concept} onChange={e => setExtra(i, "concept", e.target.value)} />
								<input type="number" min={0} step="0.01" className="wau-entrada wau-num" aria-label={conMoneda(S.precio, cur)} placeholder={S.precio} value={x.price} onChange={e => setExtra(i, "price", e.target.value)} />
								<button type="button" className="wau-btn s mini" aria-label={S.quitar} onClick={() => setExtras(xs => xs.filter((_x, j) => j !== i))}>×</button>
							</div>
						))}
						<button type="button" className="wau-btn s mini" style={{ marginTop: 4 }} onClick={() => setExtras(xs => [...xs, { concept: "", price: "" }])}>{S.agregarExtra}</button>
					</div>
					<div>
						<span className="wau-lab" style={{ display: "block", marginBottom: 3 }}>{conMoneda(S.paquetes, cur)}</span>
						{tiers.map((t, i) => {
							const total = Number(t.total);
							const ahorro = t.guests * extra - total;
							return (
								<div key={t.guests} className="wev-paquete">
									<span>{S.nInvitados(t.guests)}</span>
									<input type="number" min={0} step="0.01" className="wau-entrada wau-num" aria-label={conMoneda(S.nInvitados(t.guests), cur)} value={t.total} onChange={e => setTier(i, "total", e.target.value)} />
									<span className="wev-ahorro">{t.total.trim() === "" || isNaN(total) || extra <= 0 ? "" : ahorro > 0.004 ? ahorroCorto(ahorro) : S.sinAhorro}</span>
									<label><input type="checkbox" checked={t.freeChild} disabled={t.total.trim() === ""} onChange={e => setTier(i, "freeChild", e.target.checked)} /><span>{S.ninoGratis}</span></label>
								</div>
							);
						})}
					</div>
				</div>
				<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
					<textarea className="wau-entrada" rows={1} value={f.notes} onChange={e => set("notes", e.target.value)} /></label>
			</div>
			{error ? <p className="wau-adv" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{evento ? S.guardar : S.crear}</button>
			</div>
			</>}
		</Modal>
	);
};

export default EventForm;
